import React from 'react';

/**
 * Common PaginationBar component for high-performance server/client pagination.
 * Matches dark/light semantic design tokens across TrophyCoin.
 */
export default function PaginationBar({
  currentPage = 1,
  totalPages = 1,
  pageSize = 25,
  totalItems = 0,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [25, 50, 100],
  itemName = 'entries'
}) {
  const safeCurrentPage = Math.max(1, Math.min(currentPage, totalPages || 1));
  const start = totalItems === 0 ? 0 : (safeCurrentPage - 1) * pageSize + 1;
  const end = Math.min(safeCurrentPage * pageSize, totalItems);

  // Generate page numbers with ellipses
  const pages = [];
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else {
    pages.push(1);
    if (safeCurrentPage > 3) pages.push('...');
    const pStart = Math.max(2, safeCurrentPage - 1);
    const pEnd = Math.min(totalPages - 1, safeCurrentPage + 1);
    for (let i = pStart; i <= pEnd; i++) {
      if (!pages.includes(i)) pages.push(i);
    }
    if (safeCurrentPage < totalPages - 2) pages.push('...');
    if (!pages.includes(totalPages)) pages.push(totalPages);
  }

  return (
    <div
      data-testid="pagination-bar"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 18px',
        borderTop: '1px solid var(--border-color)',
        background: 'rgba(128, 128, 128, 0.04)',
        flexWrap: 'wrap',
        gap: '12px',
        fontSize: '12px',
        color: 'var(--text-main)'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
        <span style={{ color: 'var(--text-mute)' }}>
          Showing <strong style={{ color: 'var(--text-main)' }}>{start}</strong> to <strong style={{ color: 'var(--text-main)' }}>{end}</strong> of <strong style={{ color: 'var(--text-main)' }}>{totalItems.toLocaleString()}</strong> {itemName}
        </span>
        {onPageSizeChange && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ color: 'var(--text-mute)', fontSize: '11px' }}>Per page:</span>
            <select
              aria-label="Rows per page"
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              style={{
                background: 'var(--card-bg, #1e293b)',
                color: 'var(--text-main, #f8fafc)',
                border: '1px solid var(--border-color, #334155)',
                borderRadius: '6px',
                padding: '4px 8px',
                fontSize: '12px',
                cursor: 'pointer'
              }}
            >
              {pageSizeOptions.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
        <button
          type="button"
          disabled={safeCurrentPage <= 1}
          onClick={() => onPageChange(1)}
          style={{
            background: 'var(--card-bg, #1e293b)',
            border: '1px solid var(--border-color, #334155)',
            color: safeCurrentPage <= 1 ? 'var(--text-mute, #64748b)' : 'var(--text-main, #f8fafc)',
            padding: '5px 9px',
            borderRadius: '6px',
            cursor: safeCurrentPage <= 1 ? 'not-allowed' : 'pointer',
            fontSize: '12px',
            opacity: safeCurrentPage <= 1 ? 0.5 : 1
          }}
        >
          « First
        </button>
        <button
          type="button"
          disabled={safeCurrentPage <= 1}
          onClick={() => onPageChange(safeCurrentPage - 1)}
          style={{
            background: 'var(--card-bg, #1e293b)',
            border: '1px solid var(--border-color, #334155)',
            color: safeCurrentPage <= 1 ? 'var(--text-mute, #64748b)' : 'var(--text-main, #f8fafc)',
            padding: '5px 10px',
            borderRadius: '6px',
            cursor: safeCurrentPage <= 1 ? 'not-allowed' : 'pointer',
            fontSize: '12px',
            opacity: safeCurrentPage <= 1 ? 0.5 : 1
          }}
        >
          ‹ Prev
        </button>
        {pages.map((p, idx) => (
          p === '...' ? (
            <span key={`ellipsis-${idx}`} style={{ padding: '0 4px', color: 'var(--text-mute)' }}>...</span>
          ) : (
            <button
              key={p}
              type="button"
              onClick={() => onPageChange(p)}
              style={{
                background: safeCurrentPage === p ? 'var(--primary-color, #0284c7)' : 'var(--card-bg, #1e293b)',
                border: `1px solid ${safeCurrentPage === p ? 'var(--primary-color, #0284c7)' : 'var(--border-color, #334155)'}`,
                color: safeCurrentPage === p ? '#ffffff' : 'var(--text-main, #f8fafc)',
                padding: '5px 10px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: safeCurrentPage === p ? 700 : 500,
                fontSize: '12px'
              }}
            >
              {p}
            </button>
          )
        ))}
        <button
          type="button"
          disabled={safeCurrentPage >= totalPages}
          onClick={() => onPageChange(safeCurrentPage + 1)}
          style={{
            background: 'var(--card-bg, #1e293b)',
            border: '1px solid var(--border-color, #334155)',
            color: safeCurrentPage >= totalPages ? 'var(--text-mute, #64748b)' : 'var(--text-main, #f8fafc)',
            padding: '5px 10px',
            borderRadius: '6px',
            cursor: safeCurrentPage >= totalPages ? 'not-allowed' : 'pointer',
            fontSize: '12px',
            opacity: safeCurrentPage >= totalPages ? 0.5 : 1
          }}
        >
          Next ›
        </button>
        <button
          type="button"
          disabled={safeCurrentPage >= totalPages}
          onClick={() => onPageChange(totalPages)}
          style={{
            background: 'var(--card-bg, #1e293b)',
            border: '1px solid var(--border-color, #334155)',
            color: safeCurrentPage >= totalPages ? 'var(--text-mute, #64748b)' : 'var(--text-main, #f8fafc)',
            padding: '5px 9px',
            borderRadius: '6px',
            cursor: safeCurrentPage >= totalPages ? 'not-allowed' : 'pointer',
            fontSize: '12px',
            opacity: safeCurrentPage >= totalPages ? 0.5 : 1
          }}
        >
          Last »
        </button>
      </div>
    </div>
  );
}
